// Hook de toasts al estilo shadcn, reducido para TORA.
// Patron original: https://ui.shadcn.com/docs/components/toast
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { ToastProps } from "@/components/ui/toast";

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 5000;

type ToasterToast = ToastProps & {
  id: string;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
};

interface State {
  toasts: ToasterToast[];
}

let count = 0;
function genId(): string {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };
const removeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function dispatch(next: State): void {
  memoryState = next;
  listeners.forEach((listener) => listener(memoryState));
}

function scheduleRemove(toastId: string): void {
  if (removeTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    removeTimeouts.delete(toastId);
    dispatch({ toasts: memoryState.toasts.filter((t) => t.id !== toastId) });
  }, TOAST_REMOVE_DELAY);
  removeTimeouts.set(toastId, timeout);
}

function dismiss(toastId?: string): void {
  dispatch({
    toasts: memoryState.toasts.map((t) =>
      toastId === undefined || t.id === toastId ? { ...t, open: false } : t,
    ),
  });
}

type ToastInput = Omit<ToasterToast, "id">;

export function toast(props: ToastInput): { id: string; dismiss: () => void } {
  const id = genId();
  const next: ToasterToast = {
    ...props,
    id,
    open: true,
    onOpenChange: (open) => {
      if (!open) dismiss(id);
    },
  };
  dispatch({ toasts: [next, ...memoryState.toasts].slice(0, TOAST_LIMIT) });
  scheduleRemove(id);
  return { id, dismiss: () => dismiss(id) };
}

export function useToast(): {
  toasts: ToasterToast[];
  toast: typeof toast;
  dismiss: (id?: string) => void;
} {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return { toasts: state.toasts, toast, dismiss };
}
